import React, { useState } from 'react';
import { Journey, JourneySetup, JourneyStage, JourneyMarket } from '../../types/journey';
import { useLanguage } from '../../context/LanguageContext';
import './JourneyResetDialog.css';

interface JourneyResetDialogProps {
  journey: Journey;
  isOpen: boolean;
  onClose: () => void;
  onReset: (newSetup?: JourneySetup, reason?: string, preserveCustomizations?: boolean) => Promise<void>;
  isLoading?: boolean;
}

export const JourneyResetDialog: React.FC<JourneyResetDialogProps> = ({
  journey,
  isOpen,
  onClose,
  onReset,
  isLoading = false
}) => {
  const { t } = useLanguage();
  const [resetType, setResetType] = useState<'same' | 'new'>('same');
  const [reason, setReason] = useState('');
  const [preserveCustomizations, setPreserveCustomizations] = useState(false);
  const [newSetup, setNewSetup] = useState<JourneySetup>({
    stage: journey.stage,
    market: journey.market,
    evaluation: journey.evaluation
  });

  const handleReset = async () => {
    try {
      const setupToUse = resetType === 'new' ? newSetup : undefined;
      await onReset(setupToUse, reason.trim() || undefined, preserveCustomizations);
      onClose();
    } catch (error) {
      console.error('Error resetting journey:', error);
    }
  };

  const handleReasonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setReason(e.target.value);
  };

  const handleNewSetupChange = (field: keyof JourneySetup, value: any) => {
    setNewSetup(prev => {
      const updated = {
        ...prev,
        [field]: value
      };
      
      // If changing to "In Progress" stage and current market is not allowed, reset to IT/Tech
      if (field === 'stage' && value === 'In Progress') {
        if (updated.market === 'Social Media/Influencer' || updated.market === 'Other') {
          updated.market = 'IT/Tech';
        }
      }
      
      return updated;
    });
  };

  if (!isOpen) return null;

  return (
    <div className="journey-reset-dialog-overlay">
      <div className="journey-reset-dialog">
        <div className="journey-reset-dialog-header">
          <h2>{t('journey.resetJourneyDialogTitle')}</h2>
          <button 
            className="journey-reset-dialog-close"
            onClick={onClose}
            disabled={isLoading}
          >
            ×
          </button>
        </div>

        <div className="journey-reset-dialog-content">
          <div className="journey-reset-warning">
            <div className="journey-reset-warning-icon">⚠️</div>
            <div>
              <p><strong>{t('journey.resetJourneyDialogWarningTitle')}</strong></p>
              <p>{t('journey.resetJourneyDialogWarningDesc')}</p>
            </div>
          </div>

          <div className="journey-reset-section">
            <h3>{t('journey.resetJourneyDialogOptionsTitle')}</h3>
            <div className="journey-reset-options">
              <label className={`journey-reset-option ${resetType === 'same' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="resetType"
                  value="same"
                  checked={resetType === 'same'}
                  onChange={(e) => setResetType(e.target.value as 'same' | 'new')}
                  disabled={isLoading}
                />
                <div className="journey-reset-option-content">
                  <div className="journey-reset-option-icon">🔄</div>
                  <div className="journey-reset-option-text">
                    <strong>{t('journey.resetJourneyKeepSameSetup')}</strong>
                    <p>{t('journey.resetJourneyKeepSameSetupDesc')}</p>
                  </div>
                </div>
              </label>

              <label className={`journey-reset-option ${resetType === 'new' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="resetType"
                  value="new"
                  checked={resetType === 'new'}
                  onChange={(e) => setResetType(e.target.value as 'same' | 'new')}
                  disabled={isLoading}
                />
                <div className="journey-reset-option-content">
                  <div className="journey-reset-option-icon">✨</div>
                  <div className="journey-reset-option-text">
                    <strong>{t('journey.resetJourneyNewSetup')}</strong>
                    <p>{t('journey.resetJourneyNewSetupDesc')}</p>
                  </div>
                </div>
              </label>
            </div>
          </div>

          {resetType === 'new' && (
            <div className="journey-reset-section">
              <h3>{t('journey.resetJourneyNewSetupTitle')}</h3>
              <div className="journey-reset-new-setup">
                <div className="journey-reset-field">
                  <label>{t('journey.resetJourneyStageLabel')}</label>
                  <select
                    value={newSetup.stage}
                    onChange={(e) => handleNewSetupChange('stage', e.target.value as JourneyStage)}
                    disabled={isLoading}
                  >
                    <option value="Beginner">{t('journey.stageBeginner')}</option>
                    <option value="Have Idea">{t('journey.stageHaveIdea')}</option>
                    <option value="In Progress">{t('journey.stageInProgress')}</option>
                  </select>
                </div>

                <div className="journey-reset-field">
                  <label>{t('journey.resetJourneyMarketLabel')}</label>
                  <select
                    value={newSetup.market}
                    onChange={(e) => handleNewSetupChange('market', e.target.value as JourneyMarket)}
                    disabled={isLoading}
                  >
                    <option value="IT/Tech">{t('journey.marketITTech')}</option>
                    <option value="E-commerce">{t('journey.marketEcommerce')}</option>
                    {newSetup.stage !== 'In Progress' && (
                      <>
                        <option value="Social Media/Influencer">{t('journey.marketSocialMedia')}</option>
                        <option value="Other">{t('journey.marketOther')}</option>
                      </>
                    )}
                  </select>
                </div>
              </div>
            </div>
          )}

          {journey.isCustomized && (
            <div className="journey-reset-section">
              <label className="journey-reset-checkbox">
                <input
                  type="checkbox"
                  checked={preserveCustomizations}
                  onChange={(e) => setPreserveCustomizations(e.target.checked)}
                  disabled={isLoading}
                />
                <span>{t('journey.resetJourneyPreserveCustomizations')}</span>
              </label>
            </div>
          )}

          <div className="journey-reset-section">
            <label htmlFor="reset-reason">{t('journey.resetJourneyReasonLabel')}</label>
            <textarea
              id="reset-reason"
              value={reason}
              onChange={handleReasonChange}
              placeholder={t('journey.resetJourneyReasonPlaceholder')}
              rows={3}
              disabled={isLoading}
            />
          </div>

          <div className="journey-reset-stats">
            <p>
              <strong>{t('journey.resetJourneyCurrentJourney')}</strong> {journey.stage} → {journey.market} → {journey.persona}
            </p>
            {journey.resetCount !== undefined && journey.resetCount > 0 && (
              <p>
                <strong>{t('journey.resetJourneyPreviousResets')}</strong> {journey.resetCount}
              </p>
            )}
          </div>
        </div>

        <div className="journey-reset-dialog-actions">
          <button
            className="journey-reset-dialog-cancel"
            onClick={onClose}
            disabled={isLoading}
          >
            {t('journey.resetJourneyCancel')}
          </button>
          <button
            className="journey-reset-dialog-confirm"
            onClick={handleReset}
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="button-loading-content">
                <span className="button-loading-spinner button-loading-spinner--inverse" aria-hidden="true"></span>
                {t('journey.resetJourneyResetting')}
              </span>
            ) : (
              t('journey.resetJourneyConfirm')
            )}
          </button>
        </div>
      </div>
    </div>
  );
};